import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageBackground from '../components/PageBackgroundBeam';
import PageFooter from '../components/PageFooter';
import RADIO_DATA from '../data/radioData';
import './PageTop25Radio.css';

function formatNumber(n) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const ORIGEN_META = {
  D: { label: 'Dom\u00e9stico',     color: '#60a5fa' },
  T: { label: 'Transnacional', color: '#4ade80' },
  G: { label: 'Gobierno',      color: '#fbbf24' },
  P: { label: 'Pol\u00edtica',      color: '#c084fc' },
};

function OrigenBadge({ code }) {
  const meta = ORIGEN_META[code];
  if (!meta) return <span className="r25-origen-badge">\u2014</span>;
  return (
    <span className="r25-origen-badge" style={{ color: meta.color, borderColor: meta.color }}>
      {code}
    </span>
  );
}

function OrigenSummary({ data }) {
  const grouped = {};
  data.forEach((row) => {
    const key = row.origen || '?';
    if (!grouped[key]) grouped[key] = { inv: 0, count: 0 };
    grouped[key].inv += row.inv;
    grouped[key].count += 1;
  });

  const top25Total = data.reduce((s, r) => s + r.inv, 0);

  const entries = Object.keys(ORIGEN_META)
    .filter((k) => grouped[k])
    .map((k) => ({
      code: k,
      ...ORIGEN_META[k],
      inv: grouped[k].inv,
      count: grouped[k].count,
      pct: (grouped[k].inv / top25Total) * 100,
    }));

  const maxInv = Math.max(...entries.map((e) => e.inv));

  return (
    <div className="r25-origen-summary">
      <h3 className="r25-origen-summary-title">Resumen por Origen</h3>
      <p className="r25-origen-summary-sub">Top 25 &middot; Participaci&oacute;n</p>

      <div className="r25-origen-cards">
        {entries.map((e) => (
          <div key={e.code} className="r25-origen-card">
            <div className="r25-origen-card-header">
              <span className="r25-origen-card-badge" style={{ color: e.color, borderColor: e.color }}>
                {e.code}
              </span>
              <span className="r25-origen-card-label" style={{ color: e.color }}>{e.label}</span>
              <span className="r25-origen-card-count">{e.count} anunc.</span>
            </div>
            <div className="r25-origen-card-inv">${formatNumber(e.inv)}</div>
            <div className="r25-origen-card-bar-bg">
              <div
                className="r25-origen-card-bar"
                style={{ width: `${(e.inv / maxInv) * 100}%`, background: e.color }}
              ></div>
            </div>
            <div className="r25-origen-card-pct" style={{ color: e.color }}>
              {e.pct.toFixed(1)}%
            </div>
          </div>
        ))}
      </div>

      {/* Donut */}
      <div className="r25-origen-donut-wrap">
        <svg width="200" height="200" viewBox="0 0 200 200">
          {(() => {
            const cx = 100, cy = 100, r = 80, ir = 50;
            let cum = -Math.PI / 2;
            return entries.map((e) => {
              const angle = (e.pct / 100) * 2 * Math.PI;
              const sa = cum;
              const ea = cum + angle;
              cum = ea;
              const x1 = cx + r * Math.cos(sa);
              const y1 = cy + r * Math.sin(sa);
              const x2 = cx + r * Math.cos(ea);
              const y2 = cy + r * Math.sin(ea);
              const ix1 = cx + ir * Math.cos(ea);
              const iy1 = cy + ir * Math.sin(ea);
              const ix2 = cx + ir * Math.cos(sa);
              const iy2 = cy + ir * Math.sin(sa);
              const la = angle > Math.PI ? 1 : 0;
              const d = [
                `M ${x1} ${y1}`,
                `A ${r} ${r} 0 ${la} 1 ${x2} ${y2}`,
                `L ${ix1} ${iy1}`,
                `A ${ir} ${ir} 0 ${la} 0 ${ix2} ${iy2}`,
                'Z',
              ].join(' ');
              return (
                <path key={e.code} d={d} fill={e.color} fillOpacity="0.85"
                  stroke="rgba(15,23,42,0.8)" strokeWidth="2" />
              );
            });
          })()}
        </svg>
        <div className="r25-origen-donut-legend">
          {entries.map((e) => (
            <span key={e.code} className="r25-origen-legend-item">
              <span className="r25-origen-legend-dot" style={{ background: e.color }}></span>
              {e.label} {e.pct.toFixed(1)}%
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PageTop25Radio() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const info = RADIO_DATA[slug];

  if (!info) {
    return (
      <PageBackground>
        <div className="radio25-page">
          <p style={{ color: '#fff', textAlign: 'center', marginTop: '4rem' }}>Emisora no encontrada.</p>
          <button className="btn-back" onClick={() => navigate('/top-10-radio')} style={{ margin: '2rem auto', display: 'block' }}>
            &#8592; Top 10 Radio
          </button>
        </div>
      </PageBackground>
    );
  }

  const maxInv = info.data[0].inv;

  return (
    <PageBackground>
      <div className="radio25-page">
        <header className="radio25-header">
          <button className="btn-back" onClick={() => navigate('/top-10-radio')}>
            &#8592; Top 10 Radio
          </button>
          <div className="radio25-title-block">
            <h1 className="radio25-title">Top 25 Anunciantes &middot; {info.canal}</h1>
            <p className="radio25-subtitle">
              Radio &middot; Honduras 2025 &middot; USD &middot; ${formatNumber(info.total)}
            </p>
          </div>
          <img src={require('../assets/logo-publisearch.png')} alt="Publisearch" className="header-logo-inline" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </header>

        <div className="radio25-body">
          {/* LEFT: Table */}
          <div className="radio25-table-wrap">
            <table className="radio25-table">
              <thead>
                <tr>
                  <th className="r25-pos">#</th>
                  <th className="r25-name">Anunciante</th>
                  <th className="r25-origen-col">Origen</th>
                  <th className="r25-inv">Valor Mercado</th>
                  <th className="r25-peso">Peso</th>
                  <th className="r25-pacum">Peso Acum.</th>
                  <th className="r25-spots">Spots</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let cumPct = 0;
                  let totalTop25Inv = 0;
                  let totalTop25Spots = 0;
                  const rows = info.data.map((row, idx) => {
                    const pct = (row.inv / info.total) * 100;
                    cumPct += pct;
                    totalTop25Inv += row.inv;
                    totalTop25Spots += row.spots;
                    return (
                      <tr key={row.pos} className="r25-row" style={{ animationDelay: `${idx * 0.04}s` }}>
                        <td className="r25-pos">
                          <span className={`r25-pos-badge ${row.pos <= 3 ? 'r25-pos-top3' : ''}`}>
                            {row.pos}
                          </span>
                        </td>
                        <td className="r25-name">
                          <span className="r25-anunciante-name">{row.anunciante}</span>
                          <div className="r25-bar-bg">
                            <div className="r25-bar" style={{ width: `${(row.inv / maxInv) * 100}%` }}></div>
                          </div>
                        </td>
                        <td className="r25-origen-col">
                          <OrigenBadge code={row.origen} />
                        </td>
                        <td className="r25-inv">${formatNumber(row.inv)}</td>
                        <td className="r25-peso">{pct.toFixed(2)}%</td>
                        <td className="r25-pacum">{cumPct.toFixed(2)}%</td>
                        <td className="r25-spots">{formatNumber(row.spots)}</td>
                      </tr>
                    );
                  });
                  return (
                    <>
                      {rows}
                      <tr className="r25-total-row">
                        <td className="r25-pos"></td>
                        <td className="r25-name r25-total-label">Total Top 25</td>
                        <td className="r25-origen-col"></td>
                        <td className="r25-inv r25-total-val">${formatNumber(totalTop25Inv)}</td>
                        <td className="r25-peso r25-total-val">{(totalTop25Inv / info.total * 100).toFixed(2)}%</td>
                        <td className="r25-pacum"></td>
                        <td className="r25-spots r25-total-val">{formatNumber(totalTop25Spots)}</td>
                      </tr>
                      <tr className="r25-grand-row">
                        <td className="r25-pos"></td>
                        <td className="r25-name r25-grand-label">Total Emisora</td>
                        <td className="r25-origen-col"></td>
                        <td className="r25-inv r25-grand-val">${formatNumber(info.total)}</td>
                        <td className="r25-peso r25-grand-val">100%</td>
                        <td className="r25-pacum"></td>
                        <td className="r25-spots r25-grand-val">{formatNumber(info.totalSpots)}</td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>

          {/* RIGHT: Origen Summary */}
          <OrigenSummary data={info.data} />
        </div>

        <PageFooter />
      </div>
    </PageBackground>
  );
}

export default PageTop25Radio;
